import { makeAutoObservable, observable } from 'mobx';

import { buildAxisValues } from '../../components/HeatMapChart/utils';
import type { HeatmapWidgetSettingsStore } from '../SettingsStore/HeatmapWidgetSettingsStore';
import type { HeatmapWidgetAxisParams } from '../types';

import type { HeatmapBinIndex, HeatmapDataPoint, HeatmapBinningResult } from './types';

export type HeatmapChartZAxisParams = Omit<HeatmapWidgetAxisParams, 'step'>;

export class HeatmapChartStore {
  private binnedData: HeatmapDataPoint[] = [];

  private ropRange: { min: number; max: number } | null = null;

  private mseRange: { min: number; max: number } | null = null;

  private activeRopBinsValue: HeatmapBinIndex[] = [];

  private activeMseBinsValue: HeatmapBinIndex[] = [];

  constructor(private settings: HeatmapWidgetSettingsStore) {
    makeAutoObservable<
      HeatmapChartStore,
      'binnedData' | 'ropRange' | 'mseRange' | 'activeRopBinsValue' | 'activeMseBinsValue'
    >(this, {
      binnedData: observable.ref,
      ropRange: observable.ref,
      mseRange: observable.ref,
      activeRopBinsValue: observable.ref,
      activeMseBinsValue: observable.ref,
    });
  }

  get xAxisValues(): number[] {
    const { min, max, step } = this.settings.currentXAxisParams;
    return buildAxisValues(min, max, step);
  }

  get yAxisValues(): number[] {
    const { min, max, step } = this.settings.currentYAxisParams;
    return buildAxisValues(min, max, step);
  }

  setBinningResult(result: HeatmapBinningResult) {
    this.binnedData = result.data;
    this.ropRange = result.ropRange;
    this.mseRange = result.mseRange;
    this.activeRopBinsValue = result.activeRopBins;
    this.activeMseBinsValue = result.activeMseBins;
  }

  get ropZAxis(): HeatmapChartZAxisParams {
    if (!this.ropRange) {
      return {
        min: 0,
        max: 0,
        name: this.settings.ropAxisParams.name,
        unit: this.settings.ropAxisParams.unit,
      };
    }
    return {
      min: this.ropRange.min,
      max: this.ropRange.max,
      name: this.settings.ropAxisParams.name,
      unit: this.settings.ropAxisParams.unit,
    };
  }

  get mseZAxis(): HeatmapChartZAxisParams {
    if (!this.mseRange) {
      return {
        min: 0,
        max: 0,
        name: this.settings.currentMseAxisParams.name,
        unit: this.settings.currentMseAxisParams.unit,
      };
    }
    return {
      min: this.mseRange.min,
      max: this.mseRange.max,
      name: this.settings.currentMseAxisParams.name,
      unit: this.settings.currentMseAxisParams.unit,
    };
  }

  get densityZAxis(): HeatmapChartZAxisParams {
    const points = this.data.map(({ pointsInBin }) => pointsInBin);
    if (!points.length) {
      return {
        min: 0,
        max: 0,
        name: 'Density',
        unit: { id: null, abbreviation: '' },
      };
    }

    return {
      min: 0,
      max: points.reduce((max, value) => Math.max(max, value), 0),
      name: 'Density',
      unit: { id: null, abbreviation: '' },
    };
  }

  get data(): HeatmapDataPoint[] {
    return this.binnedData;
  }

  get activeRopBins(): HeatmapBinIndex[] {
    return this.activeRopBinsValue;
  }

  get activeMseBins(): HeatmapBinIndex[] {
    return this.activeMseBinsValue;
  }
}
